import { formatOtsDateTime, type Shift, useGetShiftQuery } from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { AdminPage } from "../components/AdminPage";
import { OtsActionButton } from "../components/OtsButton";
import { OtsTableLoader } from "../components/OtsTableLoader";
import type { OtsBrand, OtsColorSchemePreference } from "../theme/OtsTheme";
import { OtsThemeRoot } from "../theme/OtsTheme";
import { nextShiftDraft, type ShiftDraft } from "./shift-form";
import { ShiftForm } from "./ShiftForm";
import { useShiftFormSave } from "./useShiftFormSave";

export type ShiftFormPageProps = {
  /** The shift to edit, or with `copy`, the one to start the next shift from. */
  shiftId?: string;
  /** Starts a new shift where `shiftId` ended, as WaterGo's copy does. */
  copy?: boolean;
  /** What a new shift starts with when there is no `shiftId`, e.g. from a ticket or dispatch. */
  draft?: ShiftDraft;
  /** The region a new shift is created in. */
  regionId?: string;
  timeZone?: string;
  /** Leaves the page: Cancel, or after a save unless `onSaved` navigates elsewhere. */
  onClose: () => void;
  onSaved?: (shift: Shift) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** Creates or edits a shift on a page of its own; any app inside the auth provider can route to it. */
export const ShiftFormPage = ({ shiftId, copy = false, draft: draftProp, regionId, timeZone, onClose, onSaved, brand, colorScheme }: ShiftFormPageProps) => {
  // A copy reads the shift it follows; an edit is loaded by the save hook as the record to save over.
  const source = useGetShiftQuery(copy && shiftId ? shiftId : skipToken);
  const draft = useMemo<ShiftDraft | undefined>(() => {
    if (shiftId && !copy) return { id: shiftId };
    if (shiftId) return source.data ? nextShiftDraft(source.data) : undefined;
    return { regionId, ...draftProp };
  }, [shiftId, copy, source.data, regionId, draftProp]);

  const form = useShiftFormSave({
    draft,
    regionId,
    onSaved: (shift) => (onSaved ?? onClose)(shift),
  });
  const stored = form.stored;
  const loading = form.loading || (copy && !source.data && !source.isError);

  return (
    <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system" className="min-h-full bg-[var(--ots-background)]">
      <AdminPage
        eyebrow="Shifts"
        title={form.editing ? "Edit Shift" : "New Shift"}
        subtitle={stored
          ? [stored.personName, formatOtsDateTime(new Date(stored.startDttm), "short", { timeZone })].filter(Boolean).join(" · ")
          : copy ? "Starts when the shift it follows ended." : undefined}
        actions={<OtsActionButton size="compact" variant="text" onClick={onClose}>Close</OtsActionButton>}>
        {loading && !stored
          ? <OtsTableLoader label="Loading the shift"/>
          : (
            <ShiftForm
              mode={form.editing ? "edit" : "new"}
              initialValues={form.initialValues}
              resetKey={stored?.id ?? draft}
              regionId={stored?.regionId ?? draft?.regionId ?? regionId}
              odometerUnits={form.odometerUnits}
              showBillableTimes={form.showBillableTimes}
              currentNames={form.currentNames}
              tabletEndDttm={stored?.altEndDttm}
              timeZone={timeZone}
              error={form.error}
              submitting={form.saving}
              onSubmit={(values, names) => void form.submit(values, names)}
              onCancel={onClose}/>
          )}
      </AdminPage>
    </OtsThemeRoot>
  );
};
